/**
 * Play-screen chrome: aspect-fit scaling, fullscreen, saves and game controls.
 *
 * The engine owns canvas.width/height (SDL sets them to the render resolution).
 * We only ever touch the CSS box, so the drawing buffer keeps its native size
 * while its CSS box aspect-fits the screen. `image-rendering: pixelated` keeps
 * fractional upscales unsmoothed. The shell forwards normalized pointer
 * coordinates through the client rect, so scaling keeps the mouse accurate.
 *
 * The chrome is `position: fixed` and mostly transparent: it overlays the game
 * rather than taking a slice of the stage, so the framebuffer maths never has
 * to account for it — in a window or in fullscreen.
 */

import { currentViewport, presentedFit, resolveDisplayMode, type DisplayMode } from '../config';
import { $, toast } from './dom';
import { mountMobilePresentation, type MobilePresentation } from './mobile-presentation';

export interface Hud {
  fit(): void;
  /**
   * Pinch zoom. In the portrait phone layout this scales the presented map;
   * everywhere else (tablets, landscape phones, any touch screen too wide for
   * the phone layout) it magnifies the plain canvas in place. Returns false
   * when nothing can zoom, so the caller leaves the gesture to the engine.
   */
  transformMap(factor: number, previousX: number, previousY: number, x: number, y: number): boolean;
  /** The framebuffer the engine was booted with; drives the scale maths. */
  setMode(mode: DisplayMode): void;
  isFullscreen(): boolean;
  destroy(): void;
}

export interface HudHandlers {
  onSaves: () => void;
  /**
   * The save editor's own entry point. It opens the same archive, in the mode
   * where Edit leads each row — the editor used to be reachable only as one of
   * five identical links inside a slot, which is not a place anyone looks for a
   * feature they have not been told about.
   */
  onSaveEdit: () => void;
  onSettings: () => void;
  /** Back to the gate screen. There is no way to tear the engine down in
   *  place, so this is a reload; saves are flushed by the beforeunload sync. */
  onQuit: () => void;
  /** Persisted so the next session starts the way this one ended. */
  onFullscreenChange?: (on: boolean) => void;
  /** The resized surface would suit a different framebuffer than the one running. */
  onResizeAvailable?: (target: DisplayMode) => void;
  /** 0 stock full frame, 1 enlarged map + stock HUD, 2 fitted inventory + HUD. */
  portraitMode?: () => number;
  /** Routes presses on the reflowed portrait HUD back to engine coordinates. */
  onPointerEvent?: (normalizedX: number, normalizedY: number, buttons: number) => void;
  /** Reports the device actually used for the latest pointer interaction. */
  onInputModality?: (touchLike: boolean) => void;
  /** True while Fallout's turn-based combat state is active. */
  inCombat?: () => boolean;
  /** Number of active stock or mod-added status indicators. */
  indicatorCount?: () => number;
  /** The engine's persistent map-cursor mode; see `EmModule._rr_mouse_mode`. */
  mouseMode?: () => number;
  /** True when the most recent pointer interaction came from touch or a pen. */
  isTouchInput?: () => boolean;
  /** Switches stock game screens from the persistent portrait HUD. */
  onNavigation?: (target: number) => void;
  /** Current stock character-editor skill state for the portrait thumb dock. */
  characterSkillState?: () => { active: boolean; index: number; value: number; points: number };
  /** Queues the stock character editor's decrease/increase command. */
  onCharacterSkillAdjust?: (delta: number) => void;
}

/**
 * Fullscreen target.
 *
 * `#stage` is the presentation surface, but it is *not* the element we make
 * fullscreen: the HUD, the save archive and the settings overlay are all
 * siblings of it, and the fullscreen element is the only subtree the browser
 * paints. Fullscreening the document root keeps every one of them reachable,
 * and `#stage` is `position: fixed; inset: 0`, so it fills the fullscreen
 * viewport exactly as it fills the window.
 */
const fullscreenTarget = () => document.documentElement;

export function mountHud(canvas: HTMLCanvasElement, handlers: HudHandlers, initialMode?: DisplayMode): Hud {
  const stage = $('#stage');
  const hud = $('#hud');
  const fsBtn = $<HTMLButtonElement>('#btn-fullscreen');

  /** The framebuffer we asked the engine for. Authoritative, because SDL may
   *  hand back a drawing buffer that is dpr-multiplied. */
  let mode: DisplayMode | null = initialMode ?? null;
  let applying = false;
  let mobilePresentation: MobilePresentation | null = null;

  /**
   * Canvas magnification for touch layouts that are not the portrait phone
   * one. An iPad, or a phone held sideways, keeps the desktop framebuffer with
   * the stock HUD baked in; the presentation layer never mounts there, so its
   * pinch handling has nothing to drive. This scales the fitted canvas with a
   * CSS transform instead. Layout size is untouched, so the ResizeObserver
   * and SDL never see it, and pointer maths keep working because
   * getBoundingClientRect already reports the transformed box.
   *
   * The offset is kept relative to the fitted (centered) box, in CSS px, and
   * is clamped so the magnified canvas always covers the fitted one: no black
   * band can open up on a side that was painted before the pinch.
   */
  let canvasZoom = 1;
  let canvasPanX = 0;
  let canvasPanY = 0;

  const applyCanvasZoom = () => {
    applying = true;
    if (canvasZoom <= 1) {
      canvasZoom = 1;
      canvasPanX = 0;
      canvasPanY = 0;
      canvas.style.removeProperty('transform');
      canvas.style.removeProperty('transform-origin');
    } else {
      const baseW = canvas.offsetWidth;
      const baseH = canvas.offsetHeight;
      canvasPanX = Math.min(0, Math.max(baseW - baseW * canvasZoom, canvasPanX));
      canvasPanY = Math.min(0, Math.max(baseH - baseH * canvasZoom, canvasPanY));
      canvas.style.transformOrigin = '0 0';
      canvas.style.transform = `translate(${canvasPanX}px, ${canvasPanY}px) scale(${canvasZoom})`;
    }
    queueMicrotask(() => (applying = false));
  };

  const resetCanvasZoom = () => {
    if (canvasZoom === 1 && !canvas.style.transform) return;
    canvasZoom = 1;
    applyCanvasZoom();
  };

  const transformCanvas = (factor: number, previousClientX: number, previousClientY: number, clientX: number, clientY: number) => {
    if (!Number.isFinite(factor) || factor <= 0) return false;
    const next = Math.min(3, Math.max(1, canvasZoom * factor));
    const appliedFactor = next / canvasZoom;
    // Work in the fitted box's own frame: its client origin is the transformed
    // box's origin minus the current pan, which avoids reading layout back
    // between two pointermoves in the same frame.
    const rect = canvas.getBoundingClientRect();
    const originX = rect.left - canvasPanX;
    const originY = rect.top - canvasPanY;
    const previousX = previousClientX - originX;
    const previousY = previousClientY - originY;
    const currentX = clientX - originX;
    const currentY = clientY - originY;
    // Keep the world point that was under the previous pinch centre under the
    // current one: this is a zoom about the fingers plus a two-finger pan.
    canvasPanX = currentX - (previousX - canvasPanX) * appliedFactor;
    canvasPanY = currentY - (previousY - canvasPanY) * appliedFactor;
    canvasZoom = next;
    applyCanvasZoom();
    return true;
  };

  /**
   * How many device pixels the buffer already holds per game pixel.
   *
   * SDL asks for a high-DPI drawable, so a 852×480 game arrives as a 1704×960
   * buffer on a 2× display. Measured rather than assumed: this factor decides
   * whether a given magnification lands on exact pixel boundaries.
   */
  const bufferScale = (): number => {
    if (!mode || !canvas.width) return 1;
    const ratio = canvas.width / mode.width;
    // Snap to a whole number; anything else means the engine changed resolution
    // on its own, in which case the buffer is the only truth left.
    const whole = Math.round(ratio);
    return whole >= 1 && Math.abs(ratio - whole) < 0.02 ? whole : 1;
  };

  const fit = () => {
    const dpr = window.devicePixelRatio || 1;
    const deviceW = (stage.clientWidth || window.innerWidth) * dpr;
    const deviceH = (stage.clientHeight || window.innerHeight) * dpr;
    // A hidden or not-yet-laid-out tab reports a zero viewport; sizing the
    // canvas to 0 there would leave it collapsed after the tab comes back.
    if (deviceW < 1 || deviceH < 1) return;
    const scale = bufferScale();
    const gameW = (canvas.width || 640) / scale;
    const gameH = (canvas.height || 480) / scale;
    if (mobilePresentation?.fit(gameW, gameH)) {
      applying = true;
      // The portrait presenter owns geometry now; a leftover canvas transform
      // from a landscape pinch would double up with its own zoom.
      resetCanvasZoom();
      queueMicrotask(() => (applying = false));
      return;
    }
    const f = presentedFit(gameW, gameH, { deviceW, deviceH, dpr }, scale);
    applying = true;
    // The CSS value may be a half-pixel (a 1704px buffer at dpr 2 is 852 CSS
    // px); what matters is that buffer × m lands on whole device pixels, which
    // by construction it does.
    canvas.style.width = `${f.cssWidth}px`;
    canvas.style.height = `${f.cssHeight}px`;
    // The fitted box may have moved or resized; re-clamp the magnification
    // against it so the pinch survives a rotation or window resize sanely.
    if (canvasZoom > 1) applyCanvasZoom();
    queueMicrotask(() => (applying = false));
  };

  /**
   * When the surface grows enough that a different framebuffer would fit it,
   * say so once — and only once per size, because the alternative is a toast on
   * every pixel of a window drag.
   */
  let offeredKey = '';
  let offerTimer: number | undefined;
  const maybeOfferResize = () => {
    if (!mode) return;
    window.clearTimeout(offerTimer);
    offerTimer = window.setTimeout(() => {
      const target = resolveDisplayMode(mode!.id, currentViewport());
      const key = `${target.width}x${target.height}`;
      if (key === `${mode!.width}x${mode!.height}` || key === offeredKey) return;
      offeredKey = key;
      handlers.onResizeAvailable?.(target);
    }, 1200);
  };

  const onStageResize = () => {
    fit();
    maybeOfferResize();
  };

  const ro = new ResizeObserver(onStageResize);
  ro.observe(stage);
  window.addEventListener('resize', onStageResize);

  // SDL can resize the drawing buffer at any time (mode switches, hi-res patch),
  // and it also writes its own style.width/height, which would override ours.
  const mo = new MutationObserver(() => {
    if (!applying) fit();
  });
  mo.observe(canvas, { attributes: true, attributeFilter: ['width', 'height', 'style'] });

  // Reveal the HUD when the pointer is near the top edge; keep it out of the way
  // the rest of the time so it never competes with the game's own interface.
  const onMove = (e: PointerEvent) => hud.classList.toggle('show', e.clientY < 64);
  window.addEventListener('pointermove', onMove);

  // --- fullscreen -----------------------------------------------------------

  /**
   * ESC belongs to the game — it is the engine's own cancel/menu key. Chrome's
   * Keyboard Lock hands it to the page while fullscreen, and keeps the browser
   * default for leaving fullscreen on *held* Escape, so nothing is taken away.
   * Unsupported elsewhere, where ESC simply leaves fullscreen as usual.
   */
  const lockEscape = (on: boolean) => {
    const kb = (navigator as Navigator & { keyboard?: { lock(k: string[]): Promise<void>; unlock(): void } }).keyboard;
    if (!kb) return;
    if (on) void kb.lock(['Escape']).catch(() => {});
    else kb.unlock();
  };

  const syncFullscreen = () => {
    const on = !!document.fullscreenElement;
    fsBtn.textContent = on ? 'Exit fullscreen' : 'Fullscreen';
    lockEscape(on);
    handlers.onFullscreenChange?.(on);
    // Refit the CSS immediately and offer a framebuffer change only when the
    // selected mode resolves to a genuinely different internal size.
    fit();
    if (!mode) return;
    const target = resolveDisplayMode(mode.id, currentViewport(on));
    if (target.width !== mode.width || target.height !== mode.height) {
      offeredKey = `${target.width}x${target.height}`;
      handlers.onResizeAvailable?.(target);
    }
  };

  const toggleFullscreen = async () => {
    fsBtn.disabled = true;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        const request = fullscreenTarget().requestFullscreen;
        if (!request) throw new Error('Fullscreen is not supported by this browser.');
        await request.call(fullscreenTarget());
      }
    } catch (error) {
      console.warn('[fullscreen] request failed:', error);
      toast('The browser blocked fullscreen. Click the game once, then try again.', 'warn', 6000);
    } finally {
      fsBtn.disabled = false;
      syncFullscreen();
    }
  };

  const onFullscreenError = () => {
    syncFullscreen();
    toast('The browser could not enter fullscreen.', 'warn', 6000);
  };

  document.addEventListener('fullscreenchange', syncFullscreen);
  document.addEventListener('fullscreenerror', onFullscreenError);

  fsBtn.addEventListener('click', () => void toggleFullscreen());
  $('#btn-saves').addEventListener('click', handlers.onSaves);
  $('#btn-saveedit').addEventListener('click', handlers.onSaveEdit);
  $('#btn-settings').addEventListener('click', handlers.onSettings);
  $('#btn-quit').addEventListener('click', handlers.onQuit);

  mobilePresentation = mountMobilePresentation(
    canvas,
    () => handlers.portraitMode?.() ?? 0,
    (x, y, buttons) => handlers.onPointerEvent?.(x, y, buttons),
    (touchLike) => handlers.onInputModality?.(touchLike),
    () => handlers.inCombat?.() ?? false,
    () => handlers.indicatorCount?.() ?? 0,
    () => handlers.mouseMode?.() ?? 0,
    () => handlers.isTouchInput?.() ?? false,
    (target) => handlers.onNavigation?.(target),
    () => handlers.characterSkillState?.() ?? { active: false, index: -1, value: -1, points: -1 },
    (delta) => handlers.onCharacterSkillAdjust?.(delta),
    fit
  );

  fit();

  return {
    fit,
    transformMap: (factor, previousX, previousY, x, y) =>
      mobilePresentation?.active()
        ? mobilePresentation.transformMap(factor, previousX, previousY, x, y)
        : transformCanvas(factor, previousX, previousY, x, y),
    setMode(next: DisplayMode) {
      mode = next;
      // A new framebuffer is a new picture; start it unmagnified.
      resetCanvasZoom();
      fit();
    },
    isFullscreen: () => !!document.fullscreenElement,
    destroy() {
      ro.disconnect();
      mo.disconnect();
      resetCanvasZoom();
      mobilePresentation?.destroy();
      window.removeEventListener('resize', onStageResize);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('fullscreenerror', onFullscreenError);
    },
  };
}
